import { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";

// Project import
import useAuth from "hooks/useAuth";
import ConfirmDialog from "components/dialogs/ConfirmDialog";
import { useTranslation } from "utils/locales/utilityFunctions";
import useNetworkStatus from "hooks/useNetworkStatus";
import { openSnackbar } from "store/reducers/snackbar";

const NetworkStatusCheck = () => {
  // --- Hooks --- //
  const { translate } = useTranslation();
  const { user, isLoggedIn } = useAuth();
  const { isOnline } = useNetworkStatus();
  const dispatch = useDispatch();
  // --- State --- //
  const [showOfflineState, setShowOfflineState] = useState(false);
  const onlineStatusRef = useRef(isOnline);

  // --- Effects --- //
  // Check software version on cloud vs local
  useEffect(() => {
    if (isLoggedIn && !user && !isOnline) {
      setShowOfflineState(true);
    } else {
        setShowOfflineState(false);
    }
  }, [isOnline, user]);

  useEffect(() => {
    if (onlineStatusRef.current === isOnline) return;
    onlineStatusRef.current = isOnline;
    if (!isOnline) {
      dispatch(
        openSnackbar({
          open: true,
          message: translate("network-offline-snackbar-message"),
          variant: "alert", // we use the alert-style
          severity: "warning", // sets the color and icon according to the severity. Either: 'error' | 'info' | 'success' | 'warning'
          transition: "Fade",
        })
      );
    }
    if (isOnline) {
      dispatch(
        openSnackbar({
          open: true,
          message: translate("network-online-snackbar-message"),
          variant: "alert", // we use the alert-style
          severity: "success", // sets the color and icon according to the severity. Either: 'error' | 'info' | 'success' | 'warning'
          transition: "Fade",
        })
      );
    }
  }, [isOnline]);

  // --- Handlers --- //
  const onConfirmOfflineStatus = () => {
    setShowOfflineState(false);
    window.location.reload();
  };
  // --- Render --- //
  if (!showOfflineState || !isLoggedIn) return null;
  return (
    <ConfirmDialog
      open={true}
      maxWidth="xs"
      title={translate("network-status-check-title")}
      text={translate("network-status-check-text")}
      onConfirmText={translate("network-status-check-button-confirm-text")}
      onCancelText={translate("close")}
      onCancel={() => setShowOfflineState(false)}
      onConfirm={onConfirmOfflineStatus}
    />
  );
};

export default NetworkStatusCheck;
